/**
 * La profondeur : ce qui est ouvert sur place, en Carte comme en Hiérarchie (ticket 13 de
 * retours-testeurs, design/carte.html). Règles pures, sans
 * React ni réseau, testées dans tests/client/carte/profondeur.test.ts.
 *
 * Décisions d'Igor du 2026-09-27 :
 * - **la profondeur est un socle** : profondeur 1, le centre et ses axes, tout replié ;
 *   profondeur 2, chaque axe montre ses étapes ; profondeur 3, les étapes des étapes ;
 * - **la molette ajoute par-dessus**, une bulle à la fois, et retient son chemin :
 *   redescendre referme tout jusqu'au niveau choisi, sauf ce chemin ;
 * - une bulle que le socle ouvrait et que la molette referme **reste fermée jusqu'au prochain
 *   changement de profondeur** ;
 * - **recliquer le losange où l'on est** referme tout jusqu'à ce niveau, chemin compris
 *   (pour tout refermer : le losange 1) ;
 * - **en entrant dans une étape, la profondeur est gardée**, comptée depuis le nouveau centre.
 *
 * Rien n'est stocké (interdit n° 2) : c'est un état de l'écran, tenu par la feuille le temps
 * du dossier (`profondeurDuDossier.ts`).
 */

export interface EtatDeProfondeur {
  /** Le socle choisi au sélecteur, depuis 1. Gardé tel quel en entrant dans une étape. */
  profondeur: number;
  /** Ouvertes à la molette ou à la flèche, chemin compris, dans l'ordre où elles l'ont été. */
  molette: readonly string[];
  /** Refermées à la molette : fermées jusqu'au prochain changement de profondeur. */
  fermees: readonly string[];
}

export const AU_DEPART: EtatDeProfondeur = { profondeur: 1, molette: [], fermees: [] };

/** Les bulles connues sous la feuille, pour savoir qui contient quoi. */
export interface ArbreConnu {
  /** Les étapes de chaque bulle, dans l'ordre de lecture. */
  enfantsDe: ReadonlyMap<string, readonly string[]>;
  parentDe: ReadonlyMap<string, string | null>;
}

/** L'arbre de ces bulles ; l'ordre de la liste est l'ordre de lecture des étapes. */
export function arbreConnu(bulles: readonly { id: string; parentId: string | null }[]): ArbreConnu {
  const enfantsDe = new Map<string, string[]>();
  const parentDe = new Map<string, string | null>();
  for (const b of bulles) {
    parentDe.set(b.id, b.parentId);
    if (b.parentId) enfantsDe.set(b.parentId, [...(enfantsDe.get(b.parentId) ?? []), b.id]);
  }
  return { enfantsDe, parentDe };
}

const etapesDe = (arbre: ArbreConnu, id: string) => arbre.enfantsDe.get(id) ?? [];

/**
 * Le chemin du centre jusqu'à cette bulle : de l'étape du premier tour à elle, le centre
 * exclu. `null` si elle n'est pas sous le centre (ou si c'est lui).
 */
function cheminDepuis(arbre: ArbreConnu, centreId: string, id: string): string[] | null {
  const chemin: string[] = [];
  for (let courant: string | null | undefined = id; courant;) {
    if (courant === centreId) return chemin.length > 0 ? chemin.reverse() : null;
    chemin.push(courant);
    courant = arbre.parentDe.get(courant);
  }
  return null;
}

/** Combien de niveaux sous le centre : autant de losanges. Au moins un. */
export function niveauxSous(arbre: ArbreConnu, centreId: string): number {
  let niveaux = 0;
  let etage = [...etapesDe(arbre, centreId)];
  while (etage.length > 0) {
    niveaux += 1;
    etage = etage.flatMap((id) => etapesDe(arbre, id));
  }
  return Math.max(1, niveaux);
}

const borner = (p: number, niveaux: number) => Math.max(1, Math.min(niveaux, Math.round(p)));

/** Le losange allumé : la profondeur choisie, au plus le dernier niveau de ce centre. */
export function profondeurMontree(
  etat: EtatDeProfondeur,
  arbre: ArbreConnu,
  centreId: string
): number {
  return borner(etat.profondeur, niveauxSous(arbre, centreId));
}

/**
 * Les étapes ouvertes sous ce centre, dans l'ordre que la carte envoie au serveur (la
 * dernière de la liste garde son groupe en place, ticket 12) : d'abord ce que le socle ouvre,
 * niveau après niveau dans l'ordre de lecture, puis ce que la molette a ouvert, la dernière
 * en dernier. Seules celles qui se voient : une étape dans une étape fermée n'y est pas.
 */
export function ouvertesALaProfondeur(
  etat: EtatDeProfondeur,
  arbre: ArbreConnu,
  centreId: string
): string[] {
  const molette = new Set(etat.molette);
  const fermees = new Set(etat.fermees);
  const ouvertes = new Set<string>();
  const socle: string[] = [];

  let etage = [...etapesDe(arbre, centreId)];
  for (let niveau = 1; etage.length > 0; niveau += 1) {
    const suivant: string[] = [];
    for (const id of etage) {
      const dedans = etapesDe(arbre, id);
      if (dedans.length === 0 || fermees.has(id)) continue;
      if (!molette.has(id) && niveau >= etat.profondeur) continue;
      ouvertes.add(id);
      if (!molette.has(id)) socle.push(id);
      suivant.push(...dedans);
    }
    etage = suivant;
  }
  return [...socle, ...etat.molette.filter((id) => ouvertes.has(id))];
}

/**
 * Un losange ou une flèche. Changer de profondeur oublie ce que la molette avait refermé ;
 * recliquer celle où l'on est oublie aussi le chemin ouvert à la molette.
 */
export function choisirLaProfondeur(
  etat: EtatDeProfondeur,
  voulue: number,
  niveaux: number
): EtatDeProfondeur {
  const cible = borner(voulue, niveaux);
  if (cible === borner(etat.profondeur, niveaux)) return { ...AU_DEPART, profondeur: cible };
  return { profondeur: cible, molette: etat.molette, fermees: [] };
}

/**
 * La molette vers l'avant, ou la flèche : la bulle s'ouvre, et tout son chemin depuis le
 * centre est retenu, pour qu'elle reste visible quand la profondeur redescend. Elle passe en
 * dernier : c'est son groupe qui reste en place. Rien sur une bulle sans étapes.
 */
export function ouvrirALaMolette(
  etat: EtatDeProfondeur,
  arbre: ArbreConnu,
  centreId: string,
  id: string
): EtatDeProfondeur {
  const chemin = cheminDepuis(arbre, centreId, id);
  if (!chemin || etapesDe(arbre, id).length === 0) return etat;
  const avant = etat.molette.filter((m) => m !== id);
  const deja = new Set(avant);
  const passe = new Set(chemin);
  return {
    profondeur: etat.profondeur,
    molette: [...avant, ...chemin.filter((c) => c !== id && !deja.has(c)), id],
    fermees: etat.fermees.filter((f) => !passe.has(f)),
  };
}

/**
 * La molette vers l'arrière, ou la flèche : la bulle se referme, et reste fermée jusqu'au
 * prochain changement de profondeur. Ce que la molette avait ouvert dedans est oublié.
 */
export function fermerALaMolette(
  etat: EtatDeProfondeur,
  arbre: ArbreConnu,
  id: string
): EtatDeProfondeur {
  const partent = new Set([id]);
  for (let etage = [...etapesDe(arbre, id)]; etage.length > 0;) {
    for (const e of etage) partent.add(e);
    etage = etage.flatMap((e) => etapesDe(arbre, e));
  }
  return {
    profondeur: etat.profondeur,
    molette: etat.molette.filter((m) => !partent.has(m)),
    fermees: etat.fermees.includes(id) ? etat.fermees : [...etat.fermees, id],
  };
}
